import { useState, useEffect, useRef, useContext } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase.config";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import LoadingContext from "../contexts/loading/LoadingContext";
import Spinner from "../components/feedback/Spinner";
import { cities } from "../data/citiesData";
import {
  Button,
  FormControl,
  FormLabel,
  Switch,
  Input,
  NumberInput,
  Textarea,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Heading,
  VStack,
  Box,
  Card,
  CardBody,
  CardHeader,
  Progress,
  Badge,
  useToast,
  Flex,
  SimpleGrid,
  Image,
  Text,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import {
  ChevronRight,
  ChevronLeft,
  Home,
  Camera,
  Upload,
  CheckCircle,
  DollarSign,
  Check,
  Building,
  Map,
  Store,
  Sparkles,
  Receipt,
} from "lucide-react";
import { Alert, AlertIcon, AlertTitle, AlertDescription } from "@chakra-ui/react";

// Pakistani Real Estate Property Types
const residentialTypes = [
  { id: "lower-portion", name: "Lower Portion", icon: "🏘️", urdu: "لوئر پورشن" },
  { id: "upper-portion", name: "Upper Portion", icon: "🏠", urdu: "اپر پورشن" },
  { id: "full-house", name: "Full House", icon: "🏡", urdu: "مکمل مکان" },
  { id: "farmhouse", name: "Farmhouse", icon: "🌾", urdu: "فارم ہاؤس" },
  { id: "banglow", name: "Banglow", icon: "🏛️", urdu: "بینگلو" },
  { id: "apartment", name: "Apartment", icon: "🏢", urdu: "اپارٹمنٹ" },
];

const plotTypes = [
  { id: "residential-plot", name: "Residential Plot", icon: "📐", urdu: "رہائشی پلاٹ" },
  { id: "commercial-plot", name: "Commercial Plot", icon: "🏪", urdu: "کمرشل پلاٹ" },
  { id: "agricultural-land", name: "Agricultural Land", icon: "🌽", urdu: "زرعی زمین" },
  { id: "housing-scheme", name: "Housing Scheme Plot", icon: "🏘️", urdu: "ہاؤسنگ سکیم پلاٹ" },
];

const commercialTypes = [
  { id: "shop", name: "Shop", icon: "🛍️", urdu: "دکان" },
  { id: "office", name: "Office", icon: "💼", urdu: "آفس" },
  { id: "godown", name: "Godown/Warehouse", icon: "🏭", urdu: "گودام" },
  { id: "showroom", name: "Showroom", icon: "✨", urdu: "شو روم" },
  { id: "restaurant", name: "Restaurant/Cafe", icon: "🍽️", urdu: "ریسٹورنٹ" },
  { id: "hotel", name: "Hotel/Guest House", icon: "🏨", urdu: "ہوٹل" },
  { id: "factory", name: "Factory/Industrial Unit", icon: "🏭", urdu: "فیکٹری" },
  { id: "plaza", name: "Commercial Plaza", icon: "🏬", urdu: "پلازہ" },
];

const PropertyTabs = ({ selectedCategory, onCategoryChange }) => {
  return (
    <Flex gap={4} mb={6} justify="center" wrap="wrap">
      {[
        { id: "residential", label: "Residential", icon: Building, urdu: "رہائشی" },
        { id: "plots", label: "Plots & Land", icon: Map, urdu: "پلاٹ" },
        { id: "commercial", label: "Commercial", icon: Store, urdu: "کمرشل" },
      ].map((category) => {
        const Icon = category.icon;
        const isSelected = selectedCategory === category.id;
        return (
          <Box
            key={category.id}
            onClick={() => onCategoryChange(category.id)}
            cursor="pointer"
          >
            <Box
              px={6}
              py={3}
              borderRadius="full"
              fontWeight="semibold"
              display="flex"
              alignItems="center"
              gap={2}
              bg={isSelected ? "teal.500" : "gray.100"}
              color={isSelected ? "white" : "gray.700"}
              _hover={{
                bg: isSelected ? "teal.600" : "gray.200",
              }}
            >
              <Icon size={18} />
              <span>{category.label}</span>
              <Text as="span" fontSize="xs" opacity={0.8}>
                ({category.urdu})
              </Text>
            </Box>
          </Box>
        );
      })}
    </Flex>
  );
};

const CreateListing = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [propertyCategory, setPropertyCategory] = useState("residential");
  const [formData, setFormData] = useState({
    type: "sale",
    propertyCategory: "residential",
    propertyType: "",
    title: "",
    bedrooms: 1,
    bathrooms: 1,
    area: 0,
    marla: 0,
    kanal: 0,
    location: "",
    city: "",
    society: "",
    price: "",
    discountedPrice: "",
    offer: false,
    description: "",
    phoneNumber: "",
    whatsappNumber: "",
    features: {
      parking: false,
      garden: false,
      security: false,
      furnished: false,
      airConditioning: false,
      heating: false,
      pool: false,
      gym: false,
      laundry: false,
      backupElectricity: false,
      elevator: false,
      maintenanceStaff: false,
    },
    nearbyAmenities: {
      school: false,
      hospital: false,
      mosque: false,
      market: false,
      park: false,
      bank: false,
    },
    userRef: "",
    installmentAvailable: false,
    downPayment: "",
    monthlyPayment: "",
    installmentDuration: "",
  });

  const { buttonLoading, spinnerLoading, dispatch } = useContext(LoadingContext);
  const toast = useToast();
  const isMounted = useRef(true);
  const auth = getAuth();
  const navigate = useNavigate();

  const steps = [
    { number: 1, title: "Listing Type", icon: "🏷️" },
    { number: 2, title: "Property Details", icon: "📝" },
    { number: 3, title: "Location", icon: "📍" },
    { number: 4, title: "Features", icon: "✨" },
    { number: 5, title: "Media", icon: "📸" },
    { number: 6, title: "Review", icon: "👁️" },
  ];

  useEffect(() => {
    if (isMounted) {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          setFormData((prev) => ({
            ...prev,
            userRef: user.uid,
          }));
          dispatch({ type: "STOP_SPINNER_LOADING" });
        } else {
          navigate("/sign-in");
        }
      });
      return () => unsubscribe();
    }
  }, [auth, navigate, dispatch]);

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    
    if (type === "checkbox") {
      if (id in formData.features) {
        setFormData(prev => ({
          ...prev,
          features: { ...prev.features, [id]: checked }
        }));
      } else if (id in formData.nearbyAmenities) {
        setFormData(prev => ({
          ...prev,
          nearbyAmenities: { ...prev.nearbyAmenities, [id]: checked }
        }));
      } else {
        setFormData(prev => ({ ...prev, [id]: checked }));
      }
    } else {
      setFormData(prev => ({ ...prev, [id]: value }));
    }
  };

  const handleNumberChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + imageFiles.length > 8) {
      toast({ title: "Maximum 8 images allowed", status: "error", duration: 3000 });
      return;
    }
    
    setImageFiles(prev => [...prev, ...files]);
    const previews = files.map(file => URL.createObjectURL(file));
    setUploadedImages(prev => [...prev, ...previews]);
  };

  const removeImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    if (currentStep === 1 && !formData.type) {
      toast({ title: "Please select listing type", status: "error", duration: 3000 });
      return;
    }
    if (currentStep === 2 && (!formData.propertyType || !formData.title)) {
      toast({ title: "Please fill all required fields", status: "error", duration: 3000 });
      return;
    }
    if (currentStep === 3 && (!formData.city || !formData.location)) {
      toast({ title: "Please fill location details", status: "error", duration: 3000 });
      return;
    }
    if (currentStep === 5 && imageFiles.length === 0) {
      toast({ title: "Please upload at least one property image", status: "error", duration: 3000 });
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, steps.length));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCategoryChange = (category) => {
    setPropertyCategory(category);
    setFormData(prev => ({
      ...prev,
      propertyCategory: category,
      propertyType: "",
    }));
  };

  const calculatePricePerMarla = () => {
    if (formData.marla && formData.price) {
      return formData.price / formData.marla;
    }
    return 0;
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const requiredFields = ["propertyType", "title", "phoneNumber", "price", "city", "location"];
    for (const field of requiredFields) {
      if (!formData[field]) {
        toast({ title: "Error", description: `Please fill out ${field} field`, status: "error", duration: 3000 });
        return;
      }
    }

    if (imageFiles.length === 0) {
      toast({ title: "Error", description: "Please upload at least one property image", status: "error", duration: 3000 });
      return;
    }

    dispatch({ type: "START_LOADING" });
    
    try {
      const storeImage = async (img) => {
        return new Promise((resolve, reject) => {
          const storage = getStorage();
          const fileName = `${auth.currentUser.uid}-${img.name}-${uuidv4()}`;
          const storageRef = ref(storage, "property-images/" + fileName);
          const uploadTask = uploadBytesResumable(storageRef, img);

          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              console.log(`Upload progress: ${progress}%`);
            },
            (error) => reject(error),
            () => {
              getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => resolve(downloadURL));
            }
          );
        });
      };

      const propertyImages = await Promise.all(imageFiles.map((img) => storeImage(img)));

      const finalPrice = formData.offer && formData.discountedPrice 
        ? Number(formData.discountedPrice) 
        : Number(formData.price);

      const formDataCopy = {
        ...formData,
        price: Number(formData.price),
        discountedPrice: formData.discountedPrice ? Number(formData.discountedPrice) : 0,
        marla: Number(formData.marla) || 0,
        kanal: Number(formData.kanal) || 0,
        bedrooms: Number(formData.bedrooms) || 1,
        bathrooms: Number(formData.bathrooms) || 1,
        area: Number(formData.area) || 0,
        downPayment: formData.downPayment ? Number(formData.downPayment) : 0,
        monthlyPayment: formData.monthlyPayment ? Number(formData.monthlyPayment) : 0,
        installmentDuration: formData.installmentDuration ? Number(formData.installmentDuration) : 0,
        propertyImages,
        thumbnail: propertyImages[0],
        finalPrice: finalPrice,
        timestamp: serverTimestamp(),
        createdAt: new Date(),
      };
      
      delete formDataCopy.images;

      await addDoc(collection(db, "properties"), formDataCopy);
      dispatch({ type: "STOP_LOADING" });
      toast({ title: "Success! 🎉", description: "Your property has been listed successfully", status: "success", duration: 5000 });
      navigate(`/category/${formDataCopy.type}`);
    } catch (error) {
      console.error("Submission error:", error);
      dispatch({ type: "STOP_LOADING" });
      toast({ title: "Submission Failed", description: "Something went wrong. Please try again.", status: "error", duration: 3000 });
    }
  };

  if (spinnerLoading) return <Spinner />;

  const getPropertyTypesByCategory = () => {
    switch (propertyCategory) {
      case "residential": return residentialTypes;
      case "plots": return plotTypes;
      case "commercial": return commercialTypes;
      default: return residentialTypes;
    }
  };

  const effectivePrice = formData.offer && formData.discountedPrice 
    ? Number(formData.discountedPrice) 
    : Number(formData.price);

  const pricePerMarla = calculatePricePerMarla();

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CardBody p={8}>
            <VStack spacing={8} align="stretch">
              <Box textAlign="center">
                <Sparkles size={48} color="#319795" style={{ margin: "0 auto" }} />
                <Badge colorScheme="teal" fontSize="lg" p={2} borderRadius="lg" mt={2}>
                  Step 1 of {steps.length}
                </Badge>
                <Heading size="lg" mt={4} color="gray.800">
                  What type of listing?
                </Heading>
                <Text color="gray.500">Are you selling or renting out this property?</Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Box
                  onClick={() => setFormData(prev => ({ ...prev, type: "sale" }))}
                  borderWidth="2px"
                  borderRadius="xl"
                  p={6}
                  cursor="pointer"
                  bg={formData.type === "sale" ? "teal.50" : "white"}
                  borderColor={formData.type === "sale" ? "teal.500" : "gray.200"}
                  _hover={{
                    borderColor: "teal.400",
                    boxShadow: "md",
                  }}
                >
                  <VStack spacing={4}>
                    <Box p={4} borderRadius="full" bg={formData.type === "sale" ? "teal.100" : "gray.100"}>
                      <DollarSign size={32} color={formData.type === "sale" ? "#319795" : "#4A5568"} />
                    </Box>
                    <Heading size="md" color="gray.800">For Sale (فروخت کے لیے)</Heading>
                    <Text color="gray.500" textAlign="center">List your property for one-time sale</Text>
                    {formData.type === "sale" && <Badge colorScheme="teal" px={3} py={1}><Check size={14} /> Selected</Badge>}
                  </VStack>
                </Box>

                <Box
                  onClick={() => setFormData(prev => ({ ...prev, type: "rent" }))}
                  borderWidth="2px"
                  borderRadius="xl"
                  p={6}
                  cursor="pointer"
                  bg={formData.type === "rent" ? "teal.50" : "white"}
                  borderColor={formData.type === "rent" ? "teal.500" : "gray.200"}
                  _hover={{
                    borderColor: "teal.400",
                    boxShadow: "md",
                  }}
                >
                  <VStack spacing={4}>
                    <Box p={4} borderRadius="full" bg={formData.type === "rent" ? "teal.100" : "gray.100"}>
                      <Home size={32} color={formData.type === "rent" ? "#319795" : "#4A5568"} />
                    </Box>
                    <Heading size="md" color="gray.800">For Rent (کرائے پر)</Heading>
                    <Text color="gray.500" textAlign="center">List your property for monthly rental</Text>
                    {formData.type === "rent" && <Badge colorScheme="teal" px={3} py={1}><Check size={14} /> Selected</Badge>}
                  </VStack>
                </Box>
              </SimpleGrid>
            </VStack>
          </CardBody>
        );

      case 2:
        const propertyTypesList = getPropertyTypesByCategory();
        return (
          <CardBody p={8}>
            <VStack spacing={6} align="stretch">
              <Box>
                <Badge colorScheme="blue" fontSize="lg" p={2} borderRadius="lg">
                  Step 2 of {steps.length}
                </Badge>
                <Heading size="lg" mt={2} color="gray.800">Property Details</Heading>
                <Text color="gray.500">Tell us about your property specifications</Text>
              </Box>

              <PropertyTabs selectedCategory={propertyCategory} onCategoryChange={handleCategoryChange} />

              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color="gray.700">Property Type</FormLabel>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                  {propertyTypesList.map((type) => (
                    <Box
                      key={type.id}
                      onClick={() => setFormData(prev => ({ ...prev, propertyType: type.id }))}
                      borderWidth="2px"
                      borderRadius="lg"
                      p={4}
                      cursor="pointer"
                      bg={formData.propertyType === type.id ? "teal.50" : "white"}
                      borderColor={formData.propertyType === type.id ? "teal.500" : "gray.200"}
                      _hover={{ boxShadow: "md" }}
                    >
                      <VStack spacing={2}>
                        <Text fontSize="2xl">{type.icon}</Text>
                        <Text fontSize="sm" fontWeight="medium" textAlign="center">{type.name}</Text>
                        <Text fontSize="xs" color="gray.500">{type.urdu}</Text>
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color="gray.700">Property Title</FormLabel>
                <Input
                  size="lg"
                  id="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Beautiful 5 Marla House in DHA Phase 3"
                  bg="white"
                  borderColor="gray.300"
                  _focus={{ borderColor: "teal.500", boxShadow: "0 0 0 1px teal.500" }}
                />
              </FormControl>

              <Box p={5} bg="gray.50" borderRadius="lg">
                <Heading size="sm" mb={4} color="gray.700">Property Size</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {(propertyCategory === "residential" || propertyCategory === "plots") && (
                    <>
                      <FormControl>
                        <FormLabel fontWeight="semibold" color="gray.700">Marla</FormLabel>
                        <NumberInput
                          size="lg"
                          value={formData.marla}
                          min={0}
                          onChange={(value) => handleNumberChange("marla", value)}
                        >
                          <NumberInputField bg="white" placeholder="e.g., 5, 10, 20" />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontWeight="semibold" color="gray.700">Kanal</FormLabel>
                        <NumberInput
                          size="lg"
                          value={formData.kanal}
                          min={0}
                          onChange={(value) => handleNumberChange("kanal", value)}
                        >
                          <NumberInputField bg="white" placeholder="e.g., 1, 2, 5" />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </>
                  )}
                  <FormControl isRequired>
                    <FormLabel fontWeight="semibold" color="gray.700">Area (sq ft)</FormLabel>
                    <NumberInput
                      size="lg"
                      value={formData.area}
                      min={0}
                      onChange={(value) => handleNumberChange("area", value)}
                    >
                      <NumberInputField bg="white" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>
              </Box>

              {propertyCategory === "residential" && (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel fontWeight="semibold" color="gray.700">Bedrooms</FormLabel>
                    <NumberInput
                      size="lg"
                      value={formData.bedrooms}
                      min={0}
                      onChange={(value) => handleNumberChange("bedrooms", value)}
                    >
                      <NumberInputField bg="white" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontWeight="semibold" color="gray.700">Bathrooms</FormLabel>
                    <NumberInput
                      size="lg"
                      value={formData.bathrooms}
                      min={0}
                      onChange={(value) => handleNumberChange("bathrooms", value)}
                    >
                      <NumberInputField bg="white" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>
              )}

              {/* PRICE SECTION */}
              <Box borderWidth="2px" borderColor="teal.200" borderRadius="xl" p={6} bg="teal.50/30">
                <Heading size="md" mb={4} color="gray.800">💰 Pricing</Heading>
                
                <FormControl isRequired mb={6}>
                  <FormLabel fontWeight="semibold" color="gray.700">
                    {formData.type === "sale" ? "Price (PKR)" : "Monthly Rent (PKR)"}
                  </FormLabel>
                  <NumberInput 
                    size="lg" 
                    value={formData.price} 
                    min={0} 
                    onChange={(value) => handleNumberChange("price", value)}
                  >
                    <NumberInputField bg="white" placeholder="Enter amount in Rupees" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                {formData.type === "sale" && (
                  <>
                    {/* Installment Option */}
                    <FormControl mb={6} p={4} bg="green.50" borderRadius="lg" borderWidth="1px" borderColor="green.300">
                      <Flex align="center" justify="space-between" mb={formData.installmentAvailable ? 4 : 0}>
                        <Box flex="1">
                          <Flex align="center" gap={2} mb={2}>
                            <Receipt size={20} color="#2F855A" />
                            <Text fontWeight="bold" fontSize="md" color="gray.800">
                              Offer Easy Installments?
                            </Text>
                            <Badge colorScheme="green" fontSize="xs" px={2}>No Interest</Badge>
                          </Flex>
                          <Text fontSize="sm" color="gray.600">
                            Let buyers know they can pay in installments
                          </Text>
                          <Text fontSize="xs" color="green.600" mt={1}>
                            ✓ Interest-Free | بغیر سود کے
                          </Text>
                        </Box>
                        <Switch 
                          colorScheme="green" 
                          isChecked={formData.installmentAvailable}
                          onChange={(e) => setFormData(prev => ({ ...prev, installmentAvailable: e.target.checked }))}
                          size="lg"
                        />
                      </Flex>

                      {/* Installment Details - Only show when enabled */}
                      {formData.installmentAvailable && (
                        <Box mt={4} pt={4} borderTopWidth="1px" borderTopColor="green.200">
                          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                            <FormControl>
                              <FormLabel fontWeight="semibold" color="gray.700" fontSize="sm">
                                Down Payment (PKR) | بیعانہ
                              </FormLabel>
                              <NumberInput
                                size="md"
                                value={formData.downPayment}
                                min={0}
                                max={formData.price}
                                onChange={(value) => handleNumberChange("downPayment", value)}
                              >
                                <NumberInputField 
                                  bg="white" 
                                  placeholder="e.g., 500,000" 
                                  _placeholder={{ fontSize: "sm" }}
                                />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                Initial payment at booking
                              </Text>
                            </FormControl>

                            <FormControl>
                              <FormLabel fontWeight="semibold" color="gray.700" fontSize="sm">
                                Monthly Payment (PKR) | ماہانہ قسط
                              </FormLabel>
                              <NumberInput
                                size="md"
                                value={formData.monthlyPayment}
                                min={0}
                                onChange={(value) => handleNumberChange("monthlyPayment", value)}
                              >
                                <NumberInputField 
                                  bg="white" 
                                  placeholder="e.g., 50,000" 
                                  _placeholder={{ fontSize: "sm" }}
                                />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                Monthly installment amount
                              </Text>
                            </FormControl>

                            <FormControl>
                              <FormLabel fontWeight="semibold" color="gray.700" fontSize="sm">
                                Duration (Months) | مدت (ماہ)
                              </FormLabel>
                              <NumberInput
                                size="md"
                                value={formData.installmentDuration}
                                min={1}
                                max={360}
                                onChange={(value) => handleNumberChange("installmentDuration", value)}
                              >
                                <NumberInputField 
                                  bg="white" 
                                  placeholder="e.g., 36" 
                                  _placeholder={{ fontSize: "sm" }}
                                />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                Total payment duration
                              </Text>
                            </FormControl>
                          </SimpleGrid>

                          {/* Calculate total payment summary */}
                          {formData.downPayment && formData.monthlyPayment && formData.installmentDuration && (
                            <Box mt={4} p={3} bg="green.100" borderRadius="md">
                              <Text fontSize="sm" fontWeight="semibold" color="green.800" mb={2}>
                                Payment Summary:
                              </Text>
                              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2} fontSize="sm">
                                <Text color="green.700">
                                  Down Payment: <strong>PKR {Number(formData.downPayment).toLocaleString()}</strong>
                                </Text>
                                <Text color="green.700">
                                  Total Monthly: <strong>PKR {(Number(formData.monthlyPayment) * Number(formData.installmentDuration)).toLocaleString()}</strong>
                                </Text>
                                <Text color="green.700">
                                  Total Price: <strong>PKR {(Number(formData.downPayment) + (Number(formData.monthlyPayment) * Number(formData.installmentDuration))).toLocaleString()}</strong>
                                </Text>
                              </SimpleGrid>
                            </Box>
                          )}

                          <Alert status="info" size="sm" mt={3} borderRadius="md">
                            <AlertIcon />
                            <Text fontSize="xs">
                              Note: This is an interest-free installment plan. Buyers will contact you directly for agreement details.
                            </Text>
                          </Alert>
                        </Box>
                      )}
                    </FormControl>

                    {/* Discount Option */}
                    <FormControl display="flex" alignItems="center" justifyContent="space-between" p={4} bg="orange.50" borderRadius="lg" mb={6}>
                      <Box>
                        <Text fontWeight="semibold" color="gray.700" mb={1}>
                          Offer Discount? | تخفیف پیش کریں؟
                        </Text>
                        <Text fontSize="xs" color="gray.600">Offer a discounted price to attract more buyers</Text>
                      </Box>
                      <Switch 
                        colorScheme="orange" 
                        id="offer" 
                        isChecked={formData.offer}
                        onChange={handleInputChange}
                        size="lg"
                      />
                    </FormControl>

                    {formData.offer && (
                      <FormControl mb={6}>
                        <FormLabel fontWeight="semibold" color="gray.700">
                          Discounted Price | تخفیف شدہ قیمت (PKR)
                        </FormLabel>
                        <NumberInput 
                          size="lg" 
                          value={formData.discountedPrice} 
                          min={0} 
                          max={formData.price} 
                          onChange={(value) => handleNumberChange("discountedPrice", value)}
                        >
                          <NumberInputField bg="white" placeholder="Enter discounted price" />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                        {formData.discountedPrice && formData.price && (
                          <Text color="green.600" fontWeight="medium" mt={2}>
                            💰 Buyer saves: PKR {(Number(formData.price) - Number(formData.discountedPrice)).toLocaleString()}
                          </Text>
                        )}
                      </FormControl>
                    )}

                    {formData.marla > 0 && formData.price > 0 && (
                      <Box p={3} bg="blue.50" borderRadius="md" mt={4}>
                        <Text color="blue.700" fontWeight="semibold">
                          📊 Price per Marla: PKR {Math.round(pricePerMarla).toLocaleString()}
                        </Text>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </VStack>
          </CardBody>
        );

      case 3:
        return (
          <CardBody p={8}>
            <VStack spacing={6} align="stretch">
              <Box>
                <Badge colorScheme="purple" fontSize="lg" p={2} borderRadius="lg">
                  Step 3 of {steps.length}
                </Badge>
                <Heading size="lg" mt={2} color="gray.800">Location Details</Heading>
                <Text color="gray.500">Where is your property located?</Text>
              </Box>

              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color="gray.700">City (شہر)</FormLabel>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                  {cities.map((city) => (
                    <Box
                      key={city.id}
                      onClick={() => setFormData(prev => ({ ...prev, city: city.name }))}
                      borderWidth="2px"
                      borderRadius="lg"
                      p={3}
                      cursor="pointer"
                      bg={formData.city === city.name ? "teal.50" : "white"}
                      borderColor={formData.city === city.name ? "teal.500" : "gray.200"}
                      _hover={{ boxShadow: "md" }}
                    >
                      <Text fontWeight="medium" textAlign="center">{city.name}</Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.700">
                  Society/Area (سوسائٹی/علاقہ)
                </FormLabel>
                <Input
                  size="lg"
                  id="society"
                  value={formData.society}
                  onChange={handleInputChange}
                  placeholder="e.g., DHA, Bahria Town, Gulberg, Model Town"
                  bg="white"
                  borderColor="gray.300"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontWeight="semibold" color="gray.700">Full Address (پتہ)</FormLabel>
                <Input
                  size="lg"
                  id="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Street, Block, House Number, Landmark"
                  bg="white"
                  borderColor="gray.300"
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontWeight="semibold" color="gray.700">Phone Number (فون نمبر)</FormLabel>
                  <Input 
                    size="lg" 
                    id="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="03XX XXXXXXX" 
                    bg="white" 
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="semibold" color="gray.700">WhatsApp Number (Optional)</FormLabel>
                  <Input 
                    size="lg" 
                    id="whatsappNumber"
                    value={formData.whatsappNumber}
                    onChange={handleInputChange}
                    placeholder="03XX XXXXXXX" 
                    bg="white" 
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.700">Description (تفصیل)</FormLabel>
                <Textarea
                  size="lg"
                  id="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your property in detail... (Features, surroundings, nearby amenities, etc.)"
                  rows={5}
                  bg="white"
                />
              </FormControl>
            </VStack>
          </CardBody>
        );

      case 4:
        return (
          <CardBody p={8}>
            <VStack spacing={6} align="stretch">
              <Box>
                <Badge colorScheme="pink" fontSize="lg" p={2} borderRadius="lg">
                  Step 4 of {steps.length}
                </Badge>
                <Heading size="lg" mt={2} color="gray.800">Features & Amenities</Heading>
                <Text color="gray.500">Select all features that apply to your property</Text>
              </Box>

              <Box>
                <Heading size="md" mb={4} color="gray.700">Property Features</Heading>
                <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                  {[
                    { key: "parking", label: "Parking", icon: "🅿️", urdu: "پارکنگ" },
                    { key: "garden", label: "Garden", icon: "🌳", urdu: "باغیچہ" },
                    { key: "security", label: "24/7 Security", icon: "🚨", urdu: "سیکیورٹی" },
                    { key: "furnished", label: "Furnished", icon: "🛋️", urdu: "فرنشڈ" },
                    { key: "airConditioning", label: "Air Conditioning", icon: "❄️", urdu: "اے سی" },
                    { key: "heating", label: "Heating", icon: "🔥", urdu: "ہیٹنگ" },
                    { key: "pool", label: "Swimming Pool", icon: "🏊", urdu: "سوئمنگ پول" },
                    { key: "gym", label: "Gym", icon: "💪", urdu: "جم" },
                    { key: "laundry", label: "Laundry", icon: "🧺", urdu: "لانڈری" },
                    { key: "backupElectricity", label: "Backup Power", icon: "⚡", urdu: "بیک اپ بجلی" },
                    { key: "elevator", label: "Elevator", icon: "🛗", urdu: "لفٹ" },
                    { key: "maintenanceStaff", label: "Maintenance", icon: "🔧", urdu: "مینٹیننس" },
                  ].map((feature) => (
                    <Box
                      key={feature.key}
                      onClick={() => handleInputChange({ target: { id: feature.key, type: "checkbox", checked: !formData.features[feature.key] } })}
                      borderWidth="2px"
                      borderRadius="lg"
                      p={4}
                      cursor="pointer"
                      bg={formData.features[feature.key] ? "teal.50" : "white"}
                      borderColor={formData.features[feature.key] ? "teal.500" : "gray.200"}
                      _hover={{ boxShadow: "md" }}
                    >
                      <VStack spacing={2}>
                        <Text fontSize="2xl">{feature.icon}</Text>
                        <Text fontWeight="medium" fontSize="sm">{feature.label}</Text>
                        <Text fontSize="xs" color="gray.500">{feature.urdu}</Text>
                        {formData.features[feature.key] && <Badge colorScheme="teal">✓</Badge>}
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>

              <Box>
                <Heading size="md" mb={4} color="gray.700">Nearby Amenities</Heading>
                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                  {[
                    { key: "school", label: "School", icon: "🏫", urdu: "اسکول" },
                    { key: "hospital", label: "Hospital", icon: "🏥", urdu: "ہسپتال" },
                    { key: "mosque", label: "Mosque", icon: "🕌", urdu: "مسجد" },
                    { key: "market", label: "Market", icon: "🛒", urdu: "مارکیٹ" },
                    { key: "park", label: "Park", icon: "🌲", urdu: "پارک" },
                    { key: "bank", label: "Bank", icon: "🏦", urdu: "بینک" },
                  ].map((amenity) => (
                    <Box
                      key={amenity.key}
                      onClick={() => handleInputChange({ target: { id: amenity.key, type: "checkbox", checked: !formData.nearbyAmenities[amenity.key] } })}
                      borderWidth="2px"
                      borderRadius="lg"
                      p={4}
                      cursor="pointer"
                      bg={formData.nearbyAmenities[amenity.key] ? "teal.50" : "white"}
                      borderColor={formData.nearbyAmenities[amenity.key] ? "teal.500" : "gray.200"}
                      _hover={{ boxShadow: "md" }}
                    >
                      <VStack spacing={2}>
                        <Text fontSize="2xl">{amenity.icon}</Text>
                        <Text fontWeight="medium">{amenity.label}</Text>
                        <Text fontSize="xs" color="gray.500">{amenity.urdu}</Text>
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            </VStack>
          </CardBody>
        );

      case 5:
        return (
          <CardBody p={8}>
            <VStack spacing={6} align="stretch">
              <Box>
                <Badge colorScheme="orange" fontSize="lg" p={2} borderRadius="lg">
                  Step 5 of {steps.length}
                </Badge>
                <Heading size="lg" mt={2} color="gray.800">Property Images</Heading>
                <Text color="gray.500">Upload high-quality images (Max 8 images)</Text>
              </Box>

              <Box
                borderWidth="2px"
                borderStyle="dashed"
                borderColor="teal.300"
                borderRadius="xl"
                p={8}
                textAlign="center"
                bg="teal.50/50"
                cursor="pointer"
                _hover={{ borderColor: "teal.400", bg: "teal.50" }}
              >
                <input
                  type="file"
                  id="image-upload"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
                <label htmlFor="image-upload">
                  <VStack spacing={4}>
                    <Box p={4} borderRadius="full" bg="teal.100">
                      <Camera size={48} color="#319795" />
                    </Box>
                    <Box>
                      <Heading size="md" color="gray.700">Click to Upload Images</Heading>
                      <Text color="gray.500">JPG, PNG, or WebP (Max 8 images)</Text>
                    </Box>
                    <Button as="span" colorScheme="teal" leftIcon={<Upload size={20} />} size="lg">
                      Browse Files
                    </Button>
                  </VStack>
                </label>
              </Box>

              {uploadedImages.length > 0 && (
                <Box>
                  <Heading size="sm" mb={3} color="gray.700">
                    Uploaded Images ({uploadedImages.length}/8)
                  </Heading>
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    {uploadedImages.map((url, index) => (
                      <Box key={index} position="relative">
                        <Image
                          src={url}
                          alt={`Property ${index + 1}`}
                          objectFit="cover"
                          h="150px"
                          w="100%"
                          borderRadius="lg"
                          borderWidth="2px"
                          borderColor={index === 0 ? "teal.500" : "gray.200"}
                        />
                        {index === 0 && (
                          <Badge position="absolute" top={2} left={2} colorScheme="teal">
                            Cover
                          </Badge>
                        )}
                        <Button
                          position="absolute"
                          top={2}
                          right={2}
                          size="xs"
                          colorScheme="red"
                          borderRadius="full"
                          onClick={() => removeImage(index)}
                        >
                          ×
                        </Button>
                      </Box>
                    ))}
                  </SimpleGrid>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    First image will be displayed as cover photo
                  </Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        );

      case 6:
        const selectedPropertyType = getPropertyTypesByCategory().find(p => p.id === formData.propertyType);
        return (
          <CardBody p={8}>
            <VStack spacing={6} align="stretch">
              <Box>
                <Badge colorScheme="green" fontSize="lg" p={2} borderRadius="lg">
                  Step 6 of {steps.length} - Review
                </Badge>
                <Heading size="lg" mt={2} color="gray.800">Review Your Listing</Heading>
                <Text color="gray.500">Please review all information before submitting</Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Card>
                  <CardHeader bg="teal.50">
                    <Heading size="md">Basic Information</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack align="stretch" spacing={2}>
                      <Box>
                        <Text color="gray.500" fontSize="sm">Listing Type</Text>
                        <Text fontWeight="semibold">{formData.type === "sale" ? "For Sale" : "For Rent"}</Text>
                      </Box>
                      <Box>
                        <Text color="gray.500" fontSize="sm">Property Type</Text>
                        <Text fontWeight="semibold">{selectedPropertyType?.name} ({selectedPropertyType?.urdu})</Text>
                      </Box>
                      <Box>
                        <Text color="gray.500" fontSize="sm">Title</Text>
                        <Text fontWeight="semibold">{formData.title}</Text>
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader bg="teal.50">
                    <Heading size="md">Pricing</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack align="stretch" spacing={2}>
                      <Box>
                        <Text color="gray.500" fontSize="sm">Price</Text>
                        <Text fontWeight="bold" fontSize="xl" color="teal.600">
                          PKR {effectivePrice.toLocaleString()}
                        </Text>
                      </Box>
                      {formData.offer && formData.discountedPrice > 0 && (
                        <Box>
                          <Text color="gray.500" fontSize="sm">Original Price</Text>
                          <Text as="del" color="gray.400">PKR {Number(formData.price).toLocaleString()}</Text>
                        </Box>
                      )}
                      {formData.installmentAvailable && (
                        <Box p={3} bg="green.100" borderRadius="md">
                          <Flex align="center" gap={2} mb={2}>
                            <Receipt size={18} color="#2F855A" />
                            <Text fontWeight="semibold" color="green.800">
                              ✅ Easy Installments Available
                            </Text>
                          </Flex>
                          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2} fontSize="sm">
                            {formData.downPayment > 0 && (
                              <Text color="green.700">
                                Down Payment: PKR {Number(formData.downPayment).toLocaleString()}
                              </Text>
                            )}
                            {formData.monthlyPayment > 0 && (
                              <Text color="green.700">
                                Monthly: PKR {Number(formData.monthlyPayment).toLocaleString()}
                              </Text>
                            )}
                            {formData.installmentDuration > 0 && (
                              <Text color="green.700">
                                Duration: {formData.installmentDuration} months
                              </Text>
                            )}
                          </SimpleGrid>
                          <Text fontSize="xs" color="green.700" mt={2}>
                            Interest-Free | قسطوں میں ادائیگی ممکن
                          </Text>
                        </Box>
                      )}
                    </VStack>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader bg="teal.50">
                    <Heading size="md">Location</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack align="stretch" spacing={2}>
                      <Box>
                        <Text color="gray.500" fontSize="sm">City</Text>
                        <Text fontWeight="semibold">{formData.city}</Text>
                      </Box>
                      <Box>
                        <Text color="gray.500" fontSize="sm">Society/Area</Text>
                        <Text fontWeight="semibold">{formData.society || "Not specified"}</Text>
                      </Box>
                      <Box>
                        <Text color="gray.500" fontSize="sm">Address</Text>
                        <Text fontWeight="semibold">{formData.location}</Text>
                      </Box>
                      <Box>
                        <Text color="gray.500" fontSize="sm">Phone</Text>
                        <Text fontWeight="semibold">{formData.phoneNumber}</Text>
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader bg="teal.50">
                    <Heading size="md">Media</Heading>
                  </CardHeader>
                  <CardBody>
                    <Box>
                      <Text color="gray.500" fontSize="sm">Images Uploaded</Text>
                      <Text fontWeight="semibold" color="green.600">
                        {uploadedImages.length} image(s)
                      </Text>
                    </Box>
                    <Box mt={3}>
                      <Text color="gray.500" fontSize="sm">Selected Features</Text>
                      <Flex flexWrap="wrap" gap={2} mt={2}>
                        {Object.entries(formData.features).filter(([_, val]) => val).map(([key]) => (
                          <Badge key={key} colorScheme="teal">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </Badge>
                        ))}
                        {Object.entries(formData.features).filter(([_, val]) => val).length === 0 && (
                          <Text color="gray.400" fontSize="sm">No features selected</Text>
                        )}
                      </Flex>
                    </Box>
                  </CardBody>
                </Card>
              </SimpleGrid>

              <Alert status="info" borderRadius="lg">
                <AlertIcon />
                <Box>
                  <AlertTitle>Ready to List!</AlertTitle>
                  <AlertDescription>
                    Your property will be visible to all buyers. Interested buyers will contact you directly.
                  </AlertDescription>
                </Box>
              </Alert>
            </VStack>
          </CardBody>
        );

      default:
        return null;
    }
  };

  const progressPercentage = (currentStep / steps.length) * 100;

  return (
    <>
      <Helmet>
        <title>List Your Property | EstateGhar - Pakistan Property Listing</title>
        <meta name="description" content="List your property for sale or rent in Pakistan." />
        <link rel="canonical" href="https://www.estateghar.com/create-listing" />
      </Helmet>
      
      <Box bg="gray.50" minH="100vh" py={8}>
        <Box maxW="6xl" mx="auto" px={4}>
          <Box textAlign="center" mb={8}>
            <Heading size="2xl" color="gray.800" mb={4}>
              List Your Property in Pakistan
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Follow these simple steps to list your property and reach thousands of potential buyers
            </Text>
          </Box>

          <Card mb={8} shadow="md">
            <CardBody p={6}>
              <Box mb={6}>
                <Progress value={progressPercentage} colorScheme="teal" size="lg" borderRadius="full" mb={4} />
                <Flex justify="space-between" wrap="wrap" gap={4}>
                  {steps.map((step) => (
                    <Flex
                      key={step.number}
                      direction="column"
                      align="center"
                      cursor="pointer"
                      onClick={() => setCurrentStep(step.number)}
                    >
                      <Box
                        w="50px"
                        h="50px"
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="xl"
                        bg={currentStep >= step.number ? "teal.500" : "gray.200"}
                        color={currentStep >= step.number ? "white" : "gray.500"}
                      >
                        {step.icon}
                      </Box>
                      <Text mt={2} fontSize="sm" fontWeight={currentStep === step.number ? "bold" : "medium"}>
                        {step.title}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              </Box>
            </CardBody>
          </Card>

          <form onSubmit={submitHandler}>
            <Card shadow="xl" borderRadius="2xl" overflow="hidden" mb={8}>
              {renderStep()}
            </Card>

            <Flex justify="space-between" gap={4}>
              <Button
                size="lg"
                leftIcon={<ChevronLeft size={20} />}
                onClick={handlePrev}
                isDisabled={currentStep === 1}
                variant="outline"
              >
                Previous
              </Button>
              {currentStep < steps.length ? (
                <Button
                  size="lg"
                  rightIcon={<ChevronRight size={20} />}
                  onClick={handleNext}
                  colorScheme="teal"
                >
                  Continue
                </Button>
              ) : (
                <Button
                  size="lg"
                  rightIcon={<CheckCircle size={20} />}
                  type="submit"
                  colorScheme="green"
                  isLoading={buttonLoading}
                  loadingText="Submitting..."
                >
                  Submit Listing
                </Button>
              )}
            </Flex>
            <Text textAlign="center" mt={4} color="gray.500">
              Step {currentStep} of {steps.length}
            </Text>
          </form>
        </Box>
      </Box>
    </>
  );
};

export default CreateListing;